import type { TFunction } from 'i18next';
import { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, Link, Stack, Typography } from '@mui/material';

import { PasswordInput } from '../../components/PasswordInput';
import { APIError, idpApi } from '../../services/idp-api';

function getResetErrorMessage(error: unknown, t: TFunction<'idp'>): string {
  if (!(error instanceof APIError)) {
    return t('resetPassword.errors.default');
  }

  if (error.code === 'TOKEN_EXPIRED') {
    return t('resetPassword.errors.tokenExpired');
  }

  if (error.code === 'TOKEN_INVALID') {
    return t('resetPassword.errors.tokenInvalid');
  }

  if (error.code === 'PASSWORD_COMPROMISED') {
    return t('resetPassword.errors.passwordCompromised');
  }

  return error.message;
}

export function ResetPasswordPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [newPassword, setNewPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const token = searchParams.get('token')?.trim() ?? '';
  const isFormInvalid = useMemo(
    () => token === '' || newPassword.length < 12,
    [newPassword, token]
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);

    if (token === '') {
      setErrorMessage(t('resetPassword.missingToken'));
      return;
    }

    setIsSubmitting(true);

    try {
      await idpApi.resetPassword({ token, newPassword });
      navigate('/auth/signin?reset=success', { replace: true });
    } catch (error) {
      setErrorMessage(getResetErrorMessage(error, t));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box sx={{ minHeight: '70vh', display: 'grid', placeItems: 'center' }}>
      <Card sx={{ width: '100%', maxWidth: 520 }}>
        <CardContent>
          <Stack
            spacing={3}
            component="form"
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
          >
            <Stack spacing={1}>
              <Typography variant="h5" component="h1" fontWeight={700}>
                {t('resetPassword.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('resetPassword.subtitle')}
              </Typography>
            </Stack>

            {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

            <PasswordInput
              label={t('resetPassword.newPasswordLabel')}
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
              }}
              required
              fullWidth
            />

            <Button type="submit" variant="contained" disabled={isSubmitting || isFormInvalid}>
              {isSubmitting ? t('resetPassword.submittingButton') : t('resetPassword.submitButton')}
            </Button>

            <Typography variant="body2" color="text.secondary">
              {t('resetPassword.needNewLink')}{' '}
              <Link component={RouterLink} to="/auth/forgot-password" underline="hover">
                {t('resetPassword.requestResetLink')}
              </Link>
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
